import { ErrorState, LoadingState, StaleBanner } from '../../components/ui/AsyncState';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoveryQuery as useQuery } from '../../components/ui/useRecoveryQuery';
import { staticQueryPolicy } from '../../api/queryPolicies';
import {
  getMatchingPolicies,
  createMatchingPolicyDraft,
  activateMatchingPolicy,
} from '../../api/endpoints/admin';
import { useMutationHook } from '../../hooks/useMutationHook';
import type { MatchingPolicyConfiguration } from '../../api/types';
import { humanizeFactorName } from '../shipments/suggestionUtils';
import styles from './SettingsView.module.css';
import { Button } from '../../components/ui/Button';
import { ViewOnlyNote } from '../../components/ui/ViewOnlyNote';
import { useOpsUser } from '../ownership/opsUser';

const DEFAULT_WEIGHTS: Record<string, number> = {
  LANE_MATCH: 30.0,
  VEHICLE_MATCH: 20.0,
  CAPACITY_MATCH: 10.0,
  AVAILABILITY: 15.0,
  LANE_HISTORY: 10.0,
  ACCEPTANCE_RATE: 5.0,
  COMPLETION_RATE: 5.0,
  RESPONSE_RATE: 5.0,
};

const DEFAULT_CONFIGURATION: MatchingPolicyConfiguration = {
  eligibility: {
    requireActiveVendor: false,
    requireKycVerified: false,
    requireServiceLaneMatch: false,
    requireTruckTypeMatch: true,
    requirePayloadCapacity: true,
  },
  weights: DEFAULT_WEIGHTS,
  ranking: {
    minimumScore: 0,
    maximumCandidates: 40,
  },
};

function parseConfiguration(raw?: string): MatchingPolicyConfiguration {
  if (!raw) return DEFAULT_CONFIGURATION;
  try {
    const parsed = JSON.parse(raw) as Partial<MatchingPolicyConfiguration>;
    if (parsed.eligibility && parsed.weights && parsed.ranking) {
      return parsed as MatchingPolicyConfiguration;
    }
  } catch {
    // Fall through to the known-safe default configuration.
  }
  return DEFAULT_CONFIGURATION;
}

export function MatchingPoliciesTab() {
  const { t } = useTranslation();
  // New versions and activation are OPS_MANAGER only (R67); executives see the policy read-only.
  const { isManager } = useOpsUser();
  const { data: policies, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'matching-policies'],
    queryFn: () => getMatchingPolicies(),
    ...staticQueryPolicy,
  });

  const [isDrafting, setIsDrafting] = useState(false);
  const [weights, setWeights] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const [k, v] of Object.entries(DEFAULT_WEIGHTS)) {
      initial[k] = String(v);
    }
    return initial;
  });
  const [requireKycVerified, setRequireKycVerified] = useState(false);
  const [requireServiceLaneMatch, setRequireServiceLaneMatch] = useState(false);
  const [minimumScore, setMinimumScore] = useState('0');
  const [error, setError] = useState<string | null>(null);

  const activePolicy = policies?.find((p) => p.status === 'ACTIVE') ?? policies?.[0];
  const activeConfiguration = parseConfiguration(activePolicy?.configuration);

  const currentSum = Object.values(weights).reduce((acc, val) => {
    const n = Number(val);
    return acc + (Number.isNaN(n) ? 0 : n);
  }, 0);

  const roundedSum = Math.round(currentSum * 100) / 100;
  const isSumValid = Math.abs(roundedSum - 100.0) < 0.001;

  const saveMut = useMutationHook(
    async () => {
      const parsedWeights: Record<string, number> = {};
      for (const [k, v] of Object.entries(weights)) {
        parsedWeights[k] = Number(v);
      }
      const draft = await createMatchingPolicyDraft({
        name: activePolicy?.name || 'DEFAULT_FTL_MATCHING',
        configuration: {
          eligibility: {
            ...activeConfiguration.eligibility,
            requireKycVerified,
            requireServiceLaneMatch,
          },
          weights: parsedWeights,
          ranking: {
            ...activeConfiguration.ranking,
            minimumScore: Number(minimumScore),
          },
        },
      });
      return await activateMatchingPolicy(draft.id);
    },
    {
      successMessage: 'New matching policy version created and activated successfully.',
      invalidateKeys: [['admin', 'matching-policies'], ['admin', 'matching'], ['admin', 'service-lanes', 'suggestions']],
      onSuccess: () => {
        setIsDrafting(false);
        setError(null);
      },
    },
  );

  const handleWeightChange = (factor: string, val: string) => {
    setWeights((prev) => ({ ...prev, [factor]: val }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!minimumScore.trim()) {
      setError('Minimum score cutoff is required.');
      return;
    }
    const minScoreNum = Number(minimumScore);
    if (isNaN(minScoreNum) || minScoreNum < 0 || minScoreNum > 100) {
      setError('Minimum score cutoff must be between 0% and 100%.');
      return;
    }
    if (!isSumValid) {
      setError(`Weights must sum to exactly 100.0%. Current sum: ${roundedSum}%`);
      return;
    }
    setError(null);
    saveMut.mutate(undefined);
  };

  if (isLoading) return <LoadingState label={t('settings.matchingPolicies.loading')} />;
  if (isError && !policies) return <ErrorState title={t('settings.matchingPolicies.loadError')} onRetry={() => refetch()} />;

  return (
    <div>
      {isError && <StaleBanner onRetry={() => refetch()} />}
      {!isManager && <ViewOnlyNote />}
      {/* Active Policy Card */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div>
            <h3 className={styles.cardTitle}>{activePolicy?.name ?? 'Default Matching Policy'}</h3>
            <p className={styles.cardHint}>
              {t('settings.matchingPolicies.activeHint')}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className={styles.versionBadge}>{t('settings.matchingPolicies.versionLabel', { version: activePolicy?.version ?? 1 })}</span>
            {isManager && !isDrafting && (
              <Button
                type="button"
                variant="secondary" size="sm"
                onClick={() => {
                  const loaded: Record<string, string> = {};
                  for (const [k, v] of Object.entries(activeConfiguration.weights)) {
                    loaded[k] = String(v);
                  }
                  setWeights(loaded);
                  setRequireKycVerified(activeConfiguration.eligibility?.requireKycVerified ?? false);
                  setRequireServiceLaneMatch(activeConfiguration.eligibility?.requireServiceLaneMatch ?? false);
                  setMinimumScore(String(activeConfiguration.ranking?.minimumScore ?? 0));
                  setIsDrafting(true);
                }}
              >
                {t('settings.matchingPolicies.newVersion')}
              </Button>
            )}
          </div>
        </div>

        {/* Eligibility & Gating Overview */}
        <div className={styles.paramsGrid}>
          <div className={styles.paramCard}>
            <div className={styles.paramLabel}>{t('settings.matchingPolicies.kycTitle')}</div>
            <div className={styles.paramVal} style={{ fontSize: 13 }}>
              {activeConfiguration.eligibility?.requireKycVerified ? (
                <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{t('settings.matchingPolicies.kycRequired')}</span>
              ) : (
                <span style={{ color: 'var(--status-green-text)', fontWeight: 600 }}>{t('settings.matchingPolicies.kycAllowUnverified')}</span>
              )}
            </div>
          </div>
          <div className={styles.paramCard}>
            <div className={styles.paramLabel}>{t('settings.matchingPolicies.laneMatchingTitle')}</div>
            <div className={styles.paramVal} style={{ fontSize: 13 }}>
              {activeConfiguration.eligibility?.requireServiceLaneMatch ? (
                <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{t('settings.matchingPolicies.laneStrict')}</span>
              ) : (
                <span style={{ color: 'var(--status-green-text)', fontWeight: 600 }}>{t('settings.matchingPolicies.laneWeighted')}</span>
              )}
            </div>
          </div>
          <div className={styles.paramCard}>
            <div className={styles.paramLabel}>{t('settings.matchingPolicies.minimumScoreTitle')}</div>
            <div className={styles.paramVal}>{activeConfiguration.ranking?.minimumScore ?? 0}%</div>
          </div>
          <div className={styles.paramCard}>
            <div className={styles.paramLabel}>{t('settings.matchingPolicies.maxCandidatesTitle')}</div>
            <div className={styles.paramVal}>{activeConfiguration.ranking?.maximumCandidates ?? 40}</div>
          </div>
        </div>

        {/* Weights overview grid */}
        <div className={styles.weightsGrid}>
          {Object.entries(activeConfiguration.weights).map(([factor, wt]) => (
            <div key={factor} className={styles.weightCard}>
              <div className={styles.weightFactorName}>{humanizeFactorName(factor)}</div>
              <div className={styles.weightValRow}>
                <span>{wt}%</span>
              </div>
              <div className={styles.weightPctBarTrack}>
                <div className={styles.weightPctBarFill} style={{ width: `${wt}%` }} />
              </div>
            </div>
          ))}
        </div>

        {activePolicy?.updatedAt && (
          <div className={styles.meta}>
            {t('settings.matchingPolicies.activeVersionUpdated', {
              version: activePolicy.version,
              date: new Date(activePolicy.updatedAt).toLocaleString('en-IN'),
            })}
          </div>
        )}
      </div>

      {/* Drafting Modal / Inline Editor */}
      {isManager && isDrafting && (
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>
            {t('settings.matchingPolicies.draftTitle', { version: (activePolicy?.version ?? 0) + 1 })}
          </h3>
          <p className={styles.cardHint}>
            {t('settings.matchingPolicies.draftHint')}
          </p>

          <form noValidate onSubmit={handleSubmit}>
            {/* Eligibility & Gating Rules Section */}
            <div style={{ marginTop: 12, marginBottom: 18, padding: '14px 16px', background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--line)' }}>
              <h4 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
                {t('settings.matchingPolicies.eligibilityTitle')}
              </h4>
              <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--muted)' }}>
                {t('settings.matchingPolicies.eligibilityHint')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={requireKycVerified}
                    onChange={(e) => setRequireKycVerified(e.target.checked)}
                  />
                  <span>{t('settings.matchingPolicies.requireKyc')}</span>
                </label>

                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={requireServiceLaneMatch}
                    onChange={(e) => setRequireServiceLaneMatch(e.target.checked)}
                  />
                  <span>{t('settings.matchingPolicies.requireLaneMatch')}</span>
                </label>

                <div style={{ marginTop: 6, maxWidth: 220 }}>
                  <label className={styles.label} htmlFor="input-minimum-score">
                    {t('settings.matchingPolicies.minimumScoreLabel')}
                  </label>
                  <div className={styles.inputWrap}>
                    <input
                      id="input-minimum-score"
                      className={styles.input}
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={minimumScore}
                      onChange={(e) => setMinimumScore(e.target.value)}
                    />
                    <span className={styles.pctSuffix}>%</span>
                  </div>
                </div>
              </div>
            </div>

            <h4 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
              {t('settings.matchingPolicies.weightsTitle')}
            </h4>
            <div className={styles.formGrid}>
              {Object.keys(DEFAULT_WEIGHTS).map((factor) => (
                <div key={factor}>
                  <label className={styles.label} htmlFor={`input-${factor}`}>
                    {humanizeFactorName(factor)}
                  </label>
                  <div className={styles.inputWrap}>
                    <input
                      id={`input-${factor}`}
                      className={styles.input}
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={weights[factor] ?? '0'}
                      onChange={(e) => handleWeightChange(factor, e.target.value)}
                    />
                    <span className={styles.pctSuffix}>%</span>
                  </div>
                </div>
              ))}
            </div>

            <div
              className={`${styles.sumTracker} ${isSumValid ? styles.sumValid : styles.sumInvalid}`}
            >
              <span>{t('settings.matchingPolicies.totalWeight')}</span>
              <span>{t('settings.matchingPolicies.weightSum', { sum: roundedSum })}</span>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.actions}>
              <Button
                type="button"
                variant="secondary" size="sm"
                onClick={() => {
                  setIsDrafting(false);
                  setError(null);
                }}
              >
                {t('common.actions.cancel')}
              </Button>
              <button
                type="submit"
                className={styles.submit}
                disabled={!isSumValid || saveMut.isLoading}
              >
                {saveMut.isLoading ? 'Activating…' : 'Save & Activate Version'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Revision History */}
      {policies && policies.length > 0 && (
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>{t('settings.matchingPolicies.historyTitle')}</h3>
          <div className={styles.historyTableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('settings.matchingPolicies.colVersion')}</th>
                  <th>{t('settings.matchingPolicies.colStatus')}</th>
                  <th>{t('settings.matchingPolicies.colCreatedAt')}</th>
                  <th>{t('settings.matchingPolicies.colUpdatedAt')}</th>
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{t('settings.matchingPolicies.versionLabel', { version: p.version })}</td>
                    <td>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: p.status === 'ACTIVE' ? 'var(--status-green-text)' : 'var(--muted)',
                        }}
                      >
                        {p.status}
                      </span>
                    </td>
                    <td>{new Date(p.createdAt).toLocaleDateString('en-IN')}</td>
                    <td>
                      {p.updatedAt ? new Date(p.updatedAt).toLocaleString('en-IN') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
